/**
 * Which Postgres (or data plane) an /admin page primarily reads/writes.
 * Used for the top-of-page badge so operators never confuse negocio vs Oruon.
 */

export type AdminDatabaseScope =
  /** Railway Postgres principal — checkout, memberships, webhooks, admin ops. */
  | "business"
  /** Railway service `personal-finance` — Oruon ledger + mirror MP/Stripe. */
  | "personal-finance"
  /** No app Postgres (PostHog, Mux, external APIs, edge-only). */
  | "external";

export type AdminDatabaseScopeMeta = {
  scope: AdminDatabaseScope;
  /** Short badge label */
  label: string;
  /** Tooltip / title */
  title: string;
  /** Service / logical name */
  service: string;
};

const META: Record<AdminDatabaseScope, Omit<AdminDatabaseScopeMeta, "scope">> = {
  business: {
    label: "DB negocio",
    title: "Postgres de negocio (membresías, checkout, webhooks, admin ops)",
    service: "Postgres",
  },
  "personal-finance": {
    label: "DB finanzas",
    title: "Postgres personal-finance (Oruon · ledger · mirror MP/Stripe)",
    service: "personal-finance",
  },
  external: {
    label: "Sin Postgres app",
    title: "No consulta la DB de la app (analytics externos, edge, APIs de terceros)",
    service: "—",
  },
};

/** Explicit path prefixes (longest match wins). */
const PATH_SCOPE: readonly { prefix: string; scope: AdminDatabaseScope }[] = [
  { prefix: "/admin/finanzas-personales", scope: "personal-finance" },
  // Pure analytics / third-party
  { prefix: "/admin/photo-analytics", scope: "external" },
  { prefix: "/admin/media-kit-analytics", scope: "external" },
  { prefix: "/admin/mediakit-analytics", scope: "external" },
  { prefix: "/admin/mux-analytics", scope: "external" },
  { prefix: "/admin/mux-conversion", scope: "external" },
  { prefix: "/admin/web-templates-analytics", scope: "external" },
  { prefix: "/admin/conversion-health", scope: "external" },
  // Default: business DB for the rest of /admin and /dashboard
];

/**
 * Resolve database scope from location pathname.
 */
export function resolveAdminDatabaseScope(pathname: string): AdminDatabaseScope {
  const path = (pathname || "").split("?")[0].replace(/\/+$/, "") || "/";
  let best: { prefix: string; scope: AdminDatabaseScope } | null = null;
  for (const entry of PATH_SCOPE) {
    if (path === entry.prefix || path.startsWith(`${entry.prefix}/`)) {
      if (!best || entry.prefix.length > best.prefix.length) best = entry;
    }
  }
  if (best) return best.scope;
  if (path.startsWith("/admin") || path.startsWith("/dashboard")) return "business";
  return "business";
}

export function getAdminDatabaseScopeMeta(
  scope: AdminDatabaseScope,
): AdminDatabaseScopeMeta {
  const safe: AdminDatabaseScope =
    scope === "business" || scope === "personal-finance" || scope === "external"
      ? scope
      : "business";
  return { scope: safe, ...META[safe] };
}
